<script lang="ts" setup>
import sellProductIcon from "@/assets/icons/sell-product.svg?url";
import emptyOptionImage from "@/assets/img/picture-choice-empty.svg?url";
import otherOptionImage from "@/assets/img/picture-choice-other.svg?url";
import ConditionalProductPicker from "@/components/common/ConditionalProductPicker.vue";
import RichTextEditor from "@/components/text-editor/index.vue";
import { useAppStore } from "@/store/app";
import type { PictureChoiceOption } from "@/types/pictureChoice";
import {
  dateRangeShortcuts,
  dateTypeFieldFormatDayjsForCondition,
  buildProductConditionKey,
  getDateTypeFieldValue,
  getOptionIconVNode,
  includeEndText,
  includeStartText,
  parseConditionDateTimeValue,
  dateTypeFieldFormat as resolveDateTypeFieldFormat,
  dateTypeFieldFormatDayjs as resolveDateTypeFieldFormatDayjs,
  resolveConditionalFieldKind,
  shouldHideInput,
  timeFormatForUI,
  toConditionDateTimeValue,
  toConditionTimeValue,
} from "@/util/conditional-logic-helpers";
import { CONDITIONAL_OPERATIONS, PRODUCT_CONDITION_FIELD_KIND } from "@/util/constants";
import {
  PRODUCT_FIELD_SENTINEL,
  applyProductIfOperator,
  applyProductSelection,
  firstProductTargetKey,
  isPricingOptionOperator,
  isProductConditionField,
  toConditionFieldValue,
  toProductSelectValue,
  pricingOptionsForField,
  resolvePricingOptionId,
  resolveProductIfOperator,
  supportsPricingOptions,
  toProductIdentityKey,
  type ProductIfOperator,
} from "@/util/product-condition-operators";
import {
  PAYMENT_SECTION_KEY,
  buildProductCatalog,
  countSelectedProducts,
  countTotalProducts,
  keysToProductSelection,
  splitShowHideValue,
} from "@/util/product-conditional-logic";
import { toTimePickerTimestamp } from "@/util/dateTimeSerialize";
import type { GroupField } from "@/util/methods";
import { allOperationOptions, standardFields } from "@/util/methods";
import { isPictureChoiceField } from "@/util/pictureChoice";
import { PieChart01Icon, Trash02Icon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIAlert,
  UICheckbox,
  UIDatepicker,
  UIInput,
  UIModalFooter,
  UISelect,
  UITabPane,
  UITabs,
  UITimePicker,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { cloneDeep } from "lodash";
import { VNode, cloneVNode, computed, h, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

// Example URL constants (not translated - these are literal URL examples)
const URL_EXAMPLES = {
  simple: "example.com",
  https: "https://example.com",
  withPath: "example.com/path",
  fullPath: "https://www.example.com/path",
};

dayjs.extend(customParseFormat);
type FieldMode = "field" | "slide";
type ScoreFormat = "percentage" | "actual-score" | "out-of-10";
interface ConditionRow {
  selectedField: string | null;
  categoryId?: string;
  scoreFormat?: ScoreFormat;
  selectedOperation: string | null;
  inputValue: any;
  rangeStart?: any;
  rangeEnd?: any;
  includeStart: boolean;
  includeEnd: boolean;
  fieldMode?: FieldMode;
  dateFormat?: string;
}
/**
 * Builder element as consumed when building the condition field list. `GroupField`
 * does not declare these two element props, and widening the shared type is out of
 * scope here, so they are added locally where they are actually read.
 */
type ConditionalSourceField = GroupField & {
  placeholder2?: string;
  picklistOptionsImage?: Array<{ label: string }>;
};
interface ConditionalFieldOption {
  value: string;
  label: string;
  type: string;
  conditionalFieldKind?: string;
  dataType?: string;
  format?: string;
  separator?: string;
  timeFormat?: string | number;
  icon: () => VNode | null;
  picklistOptions?: string[] | null;
  picklistOptionsImage?: PictureChoiceOption[] | null;
  isPictureChoice?: boolean;
  allowCustomOption?: boolean;
}
interface ConditionalFieldGroupOption {
  key: "quizFields" | "standardFields" | "customFields";
  label: string;
  type: "group";
  children: ConditionalFieldOption[];
}
const props = defineProps({
  editIndex: {
    type: Number,
    default: 0,
  },
  type: {
    type: String,
    default: "displayCustomMessage",
  },
});

const showPhoneAlert = ref(false);
const disqualifyActionOptions = [
  { label: t("form.conditonalLogic.showCustomMessage"), value: "showCustomMessage" },
  { label: t("common.openUrl"), value: "openUrl" },
];

const disqualifyTimingOptions = [
  { label: t("common.disqualifyImmediately"), value: "disqualifyImmediately" },
  { label: t("common.disqualifyAfterSubmit"), value: "disqualifyAfterSubmit" },
];
const showHideOptions = [
  { label: t("form.conditonalLogic.hideField"), value: "Hide" },
  { label: t("form.conditonalLogic.showField"), value: "Show" },
  { label: t("form.conditonalLogic.hideFields"), value: "Hide Multiple" },
  { label: t("form.conditonalLogic.showFields"), value: "Show Multiple" },
];
const showMultiple = computed(() => hideType.value.includes("Multiple"));

const store = useAppStore();
const emit = defineEmits(["onSave"]);
const sharedSelectedOperation = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex]?.conditionalOperation || "then"
);
const disqualifyTiming = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex]?.outcome?.disqualifyTiming ||
    "disqualifyAfterSubmit"
);
const disqualifyAction = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex]?.outcome?.disqualifyAction ||
    "showCustomMessage"
);
// Persisted as a bare string for singular Show/Hide and as a list for the "Multiple"
// variants; the runtime normalizes both, so both shapes are kept here.
const outcomeValue = ref<string | string[]>(
  store.builderConfig.conditionalLogic?.[props.editIndex]?.outcome?.value || ""
);
const hideType = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex]?.outcome?.hideType || "Hide"
);
const jumpTarget = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex]?.outcome?.jumpTarget || ""
);
const CATEGORY_SCORE_FIELD = "categoryScore";
const RANKED_CATEGORY_FIELDS = new Set(["highestScore", "lowestScore"]);
const RANKED_CATEGORY_OPERATIONS = new Set<string>([
  CONDITIONAL_OPERATIONS.IS_EQUAL_TO,
  CONDITIONAL_OPERATIONS.IS_NOT_EQUAL_TO,
]);
const QUIZ_SCORE_FIELDS = new Set([
  "highestScore",
  "lowestScore",
  "overAllScore",
  CATEGORY_SCORE_FIELD,
]);
const hasConfiguredQuizCategory = (categoryId: unknown): boolean =>
  typeof categoryId === "string" &&
  !!store.category?.some(
    (category) => category.id !== "overAllScore" && category.id === categoryId
  );
const isQuizScoreField = (selectedField: unknown): boolean =>
  typeof selectedField === "string" && QUIZ_SCORE_FIELDS.has(selectedField);
const isRankedCategoryField = (selectedField: unknown): boolean =>
  typeof selectedField === "string" && RANKED_CATEGORY_FIELDS.has(selectedField);
const isNumericScoreField = (selectedField: unknown): boolean =>
  selectedField === "overAllScore" || selectedField === CATEGORY_SCORE_FIELD;
const toConditionRow = (c: any): ConditionRow => {
  const savedSelectedField = c?.selectedField ?? null;
  const legacyCategoryId = hasConfiguredQuizCategory(savedSelectedField)
    ? savedSelectedField
    : undefined;
  const selectedField = legacyCategoryId ? CATEGORY_SCORE_FIELD : savedSelectedField;
  const isBetween = c?.selectedOperation === "between";
  let inputPayload = c?.inputValue;
  if (isBetween && typeof inputPayload === "string") {
    try {
      inputPayload = JSON.parse(inputPayload);
    } catch {
      inputPayload = undefined;
    }
  }

  const hydratedRangeStart =
    c?.rangeStart !== undefined && c?.rangeStart !== null && c?.rangeStart !== ""
      ? c.rangeStart
      : isBetween
      ? inputPayload?.start
      : undefined;
  const hydratedRangeEnd =
    c?.rangeEnd !== undefined && c?.rangeEnd !== null && c?.rangeEnd !== ""
      ? c.rangeEnd
      : isBetween
      ? inputPayload?.end
      : undefined;
  const hydratedIncludeStart =
    typeof c?.includeStart === "boolean"
      ? c.includeStart
      : isBetween && typeof inputPayload?.includeStart === "boolean"
      ? inputPayload.includeStart
      : true;
  const hydratedIncludeEnd =
    typeof c?.includeEnd === "boolean"
      ? c.includeEnd
      : isBetween && typeof inputPayload?.includeEnd === "boolean"
      ? inputPayload.includeEnd
      : true;

  // Derive fieldMode for legacy Jump To rules without fieldMode
  let hydratedFieldMode: FieldMode | undefined = c?.fieldMode as FieldMode | undefined;
  if (!hydratedFieldMode && store.surveyId && props.type === "jumpTo") {
    const slideIds = new Set((store.slides || []).map((s: any) => String(s.id)));
    const selected = String(c?.selectedField || "");
    hydratedFieldMode = selected && slideIds.has(selected) ? "slide" : "field";
  }

  return {
    selectedField,
    categoryId:
      selectedField === CATEGORY_SCORE_FIELD || isRankedCategoryField(selectedField)
        ? c?.categoryId ?? legacyCategoryId
        : undefined,
    scoreFormat: isNumericScoreField(selectedField) ? c?.scoreFormat ?? "percentage" : undefined,
    selectedOperation: c?.selectedOperation ?? null,
    inputValue: isBetween ? null : c?.inputValue ?? null,
    rangeStart: hydratedRangeStart,
    rangeEnd: hydratedRangeEnd,
    includeStart: hydratedIncludeStart,
    includeEnd: hydratedIncludeEnd,
    fieldMode: hydratedFieldMode ?? (c?.fieldMode as FieldMode | undefined) ?? undefined,
    dateFormat: c?.dateFormat ?? undefined,
  };
};

const initialRawConditions =
  (store.builderConfig.conditionalLogic?.[props.editIndex]?.conditions as any[]) || [];

const conditions = ref<ConditionRow[]>(
  initialRawConditions.length
    ? initialRawConditions.map((c) => toConditionRow(c))
    : [
        {
          selectedField: null,
          selectedOperation:
            props.type === "jumpTo" || props.type === "disqualifyLead" ? "isEqualTo" : null,
          inputValue: null,
          rangeStart: undefined as any,
          rangeEnd: undefined as any,
          includeStart: true,
          includeEnd: true,
          fieldMode: store.surveyId && props.type === "jumpTo" ? "field" : undefined,
        },
      ]
);

// Container for teleporting embed modals (image/link) inside the conditional logic modal
const clEditorContainer = ref<HTMLElement | null>(null);

const addCondition = (condition, index) => {
  sharedSelectedOperation.value = condition;
  if (condition !== "then" && index === conditions.value.length - 1) {
    conditions.value.push({
      selectedField: null,
      selectedOperation:
        props.type === "jumpTo" || props.type === "disqualifyLead" ? "isEqualTo" : null,
      inputValue: null,
      rangeStart: undefined,
      rangeEnd: undefined,
      includeStart: true,
      includeEnd: true,
      fieldMode: store.surveyId && props.type === "jumpTo" ? "field" : undefined,
    });
  }
};

const deleteCondition = (index) => {
  conditions.value.splice(index, 1);
  if (conditions.value.length === 0) {
    conditions.value.push({
      selectedField: null,
      selectedOperation: null,
      inputValue: null,
      rangeStart: undefined,
      rangeEnd: undefined,
      includeStart: true,
      includeEnd: true,
      fieldMode: store.surveyId && props.type === "jumpTo" ? "field" : undefined,
    });
  }
};

const isScoreValueInvalid = (condition: ConditionRow): boolean => {
  if (
    !isNumericScoreField(condition.selectedField) ||
    (condition.scoreFormat !== "out-of-10" && condition.scoreFormat !== "percentage") ||
    shouldHideInput(condition.selectedOperation)
  ) {
    return false;
  }
  const maximumValue = condition.scoreFormat === "percentage" ? 100 : 10;
  const isValidValue = (value: unknown): boolean =>
    (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) &&
    Number.isFinite(Number(value)) && Number(value) <= maximumValue;
  return condition.selectedOperation === "between"
    ? !isValidValue(condition.rangeStart) || !isValidValue(condition.rangeEnd)
    : !isValidValue(condition.inputValue);
};

const isConditionInvalid = (condition) => {
  // Jump To (Slide basis): only slide selection is required; no value input
  if (props.type === "jumpTo" && store.surveyId && condition.fieldMode === "slide") {
    return !condition.selectedField;
  }
  if (isRankedCategoryField(condition.selectedField)) {
    return !(
      RANKED_CATEGORY_OPERATIONS.has(condition.selectedOperation) &&
      hasConfiguredQuizCategory(condition.categoryId)
    );
  }
  if (
    condition.selectedField === CATEGORY_SCORE_FIELD &&
    !hasConfiguredQuizCategory(condition.categoryId)
  ) {
    return true;
  }
  if (
    isNumericScoreField(condition.selectedField) &&
    !shouldHideInput(condition.selectedOperation) &&
    !condition.scoreFormat
  ) {
    return true;
  }
  if (shouldHideInput(condition.selectedOperation)) {
    // Value-less operators (isEmpty/isFilled/isChecked/isNotChecked/isSelected/isNotSelected)
    return false;
  }
  if (isScoreValueInvalid(condition)) {
    return true;
  }
  if (condition.selectedOperation === "between") {
    const hasStart =
      condition.rangeStart !== undefined &&
      condition.rangeStart !== null &&
      condition.rangeStart !== "";
    const hasEnd =
      condition.rangeEnd !== undefined && condition.rangeEnd !== null && condition.rangeEnd !== "";
    return !(condition.selectedField && condition.selectedOperation && hasStart && hasEnd);
  }
  return !(
    condition.selectedField &&
    condition.selectedOperation &&
    (condition.inputValue || condition.inputValue === 0)
  );
};

const isSaveDisabled = computed(
  () =>
    conditions.value.some(isConditionInvalid) ||
    (props.type !== "jumpTo" &&
      !Array.isArray(outcomeValue.value) &&
      outcomeValue.value.trim() === "") ||
    // A multi-target outcome with nothing chosen is as incomplete as an empty string one.
    (props.type !== "jumpTo" &&
      Array.isArray(outcomeValue.value) &&
      outcomeValue.value.length === 0) ||
    isProductSelectionIncomplete.value ||
    (props.type === "jumpTo" && !jumpTarget.value) ||
    !!cycleErrorMessage.value
);

// --- Cycle protection (Jump To rules) ---
const getSlideLabel = (id: string): string => {
  const index = store.slides.findIndex((s: any) => String(s.id) === String(id));
  if (index === -1) {
    return String(id);
  }
  return `P${index + 1} - ${store.slides[index].slideName}`;
};

const getSlideIdsFromRule = (rule: any): string[] => {
  if (!rule || !Array.isArray(rule?.conditions)) {
    return [];
  }
  const allSlideIds = new Set((store.slides || []).map((s: any) => String(s.id)));
  return rule.conditions
    .filter((cond: any) => {
      const sid = String(cond?.selectedField || "");
      return !!sid && (cond?.fieldMode === "slide" || allSlideIds.has(sid));
    })
    .map((cond: any) => String(cond.selectedField));
};

const computeIfSlideIds = (): string[] => {
  const allSlideIds = new Set((store.slides || []).map((s: any) => String(s.id)));
  return (conditions.value || [])
    .filter((c: any) => {
      const sid = String(c?.selectedField || "");
      return (c?.fieldMode === "slide" && !!sid) || (!c?.fieldMode && allSlideIds.has(sid));
    })
    .map((c: any) => String(c.selectedField));
};

const willIntroduceTwoWayCycle = (
  sources: string[],
  target: string
): { a: string; b: string } | null => {
  if (!store.surveyId || !target || !sources?.length) {
    return null;
  }
  const edges = new Map<string, string>();
  const rulesArr: any[] = ((store.builderConfig as any)?.conditionalLogic || []) as any[];
  // existing edges
  rulesArr.forEach((r) => {
    if (r?.outcome?.type !== "jumpTo") {
      return;
    }
    const rTarget = String(r?.outcome?.jumpTarget || "");
    if (!rTarget) {
      return;
    }
    const rSources = getSlideIdsFromRule(r);
    rSources.forEach((s) => edges.set(String(s), rTarget));
  });
  // proposed edges
  sources.forEach((s) => edges.set(String(s), String(target)));
  // detect A<->B
  for (const [a, b] of edges.entries()) {
    if (!b) {
      continue;
    }
    const back = edges.get(String(b));
    if (back === a) {
      return { a, b };
    }
  }
  return null;
};

const cycleErrorMessage = ref<string>("");

watchEffect(() => {
  if (props.type !== "jumpTo" || !store.surveyId) {
    cycleErrorMessage.value = "";
    return;
  }
  const sources = computeIfSlideIds();
  const target = String(jumpTarget.value || "");
  const pair = willIntroduceTwoWayCycle(sources, target);
  if (pair) {
    cycleErrorMessage.value = t("form.conditonalLogic.cycleError", {
      a: getSlideLabel(pair.a),
      b: getSlideLabel(pair.b),
    }) as string;
  } else {
    cycleErrorMessage.value = "";
  }
});

const onSave = () => {
  if (conditions.value.some(isScoreValueInvalid)) {
    return;
  }
  if (props.type === "jumpTo" && store.surveyId) {
    const sources = computeIfSlideIds();
    const target = String(jumpTarget.value || "");
    const pair = willIntroduceTwoWayCycle(sources, target);
    if (pair) {
      cycleErrorMessage.value = t("form.conditonalLogic.cycleError", {
        a: getSlideLabel(pair.a),
        b: getSlideLabel(pair.b),
      }) as string;
      return; // block save
    }
  }
  conditions.value = conditions.value
    .filter((condition: any) => condition.conditionalOperation !== null)
    .map((condition) => {
      if (isRankedCategoryField(condition.selectedField)) {
        return {
          selectedField: condition.selectedField,
          selectedOperation: condition.selectedOperation,
          categoryId: condition.categoryId,
        };
      }
      const scoreMetadata = {
        ...(condition.selectedField === CATEGORY_SCORE_FIELD && condition.categoryId
          ? { categoryId: condition.categoryId }
          : {}),
        ...(isNumericScoreField(condition.selectedField) &&
        !hideInput(condition) &&
        condition.scoreFormat
          ? { scoreFormat: condition.scoreFormat }
          : {}),
      };
      if (condition.selectedOperation === "between") {
        const payloadDateFormat = dateFormatForSave(condition);
        const betweenPayload = {
          start: normalizeDateConditionValueForSave(condition, condition.rangeStart),
          end: normalizeDateConditionValueForSave(condition, condition.rangeEnd),
          includeStart: !!condition.includeStart,
          includeEnd: !!condition.includeEnd,
        };
        return {
          selectedField: condition.selectedField,
          ...scoreMetadata,
          selectedOperation: condition.selectedOperation,
          inputValue: betweenPayload as any,
          ...(payloadDateFormat ? { dateFormat: payloadDateFormat } : {}),
        } as any;
      }
      if (getType(condition) === "monetory" && !isNaN(parseFloat(condition.inputValue))) {
        const payloadDateFormat = dateFormatForSave(condition);
        return {
          selectedField: condition.selectedField,
          ...scoreMetadata,
          selectedOperation: condition.selectedOperation,
          inputValue: parseFloat(condition.inputValue).toFixed(2),
          ...(payloadDateFormat ? { dateFormat: payloadDateFormat } : {}),
        } as any;
      }
      const payloadDateFormat = dateFormatForSave(condition);
      return {
        selectedField: condition.selectedField,
        ...scoreMetadata,
        selectedOperation: condition.selectedOperation,
        inputValue: normalizeDateConditionValueForSave(condition, condition.inputValue),
        ...(payloadDateFormat ? { dateFormat: payloadDateFormat } : {}),
      } as any;
    });

  const savedLogic: any = {
    conditions: conditions.value,
    outcome: { type: props.type, value: outcomeValue.value },
    conditionalOperation: sharedSelectedOperation.value,
  };
  if (props.type === "disqualifyLead") {
    savedLogic.outcome = {
      ...savedLogic.outcome,
      disqualifyAction: disqualifyAction.value,
    };

    if (store.surveyId) {
      savedLogic.outcome = {
        ...savedLogic.outcome,
        disqualifyTiming: disqualifyTiming.value,
      };
    }
  }
  if (props.type === "showHideFields") {
    savedLogic.outcome = {
      ...savedLogic.outcome,
      hideType: hideType.value,
    };
  }
  if (props.type === "jumpTo") {
    savedLogic.outcome = {
      ...savedLogic.outcome,
      jumpTarget: jumpTarget.value,
      jumpTargetType: store.surveyId ? "slide" : jumpTargetType.value,
    };
    // Two-way sync to sidebar jumpTo when basis is Slide
    if (store.surveyId) {
      const allSlideIds = store.slides.map((s) => String(s.id));
      const slideIds = (conditions.value || [])
        .filter((c: any) => {
          const sid = String(c?.selectedField || "");
          return (
            (c?.fieldMode === "slide" && !!sid) || (!c?.fieldMode && allSlideIds.includes(sid))
          );
        })
        .map((c: any) => String(c.selectedField));
      slideIds.forEach((sid: string) => {
        const slideIndex = store.slides.findIndex((s) => s.id === sid);
        if (slideIndex >= 0) {
          (store.slides[slideIndex] as any).jumpTo = jumpTarget.value;
        }
      });

      // De-duplicate Jump To rules per slide: update existing rule if one already exists
      const rulesArr: any[] = (store.builderConfig as any)?.conditionalLogic || [];
      slideIds.forEach((sid: string) => {
        const matchingIndexes = rulesArr
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => {
            if (r?.outcome?.type !== "jumpTo" || !Array.isArray(r?.conditions)) {
              return false;
            }
            return r.conditions.some(
              (c: any) =>
                (c?.fieldMode === "slide" && c?.selectedField === sid) ||
                (!c?.fieldMode && String(c?.selectedField || "") === sid)
            );
          })
          .map(({ i }) => i);

        if (matchingIndexes.length) {
          const keepIndex = Math.min(...matchingIndexes);
          // Update the kept rule's outcome to latest target
          rulesArr[keepIndex].outcome = {
            ...(rulesArr[keepIndex].outcome || {}),
            jumpTarget: jumpTarget.value,
            jumpTargetType: "slide",
            type: "jumpTo",
          };
          // Remove other duplicates except the kept one
          const toRemove = matchingIndexes.filter((idx) => idx !== keepIndex);
          toRemove
            .sort((a, b) => b - a)
            .forEach((idx) => {
              if (idx >= 0 && idx < rulesArr.length) {
                rulesArr.splice(idx, 1);
              }
            });
        }
      });
      // Persist potential dedup changes
      (store.builderConfig as any).conditionalLogic = rulesArr;

      // Upsert: write savedLogic into existing Jump To rule for the same slide if present
      let writeIndex = props.editIndex as number;
      const firstSid = slideIds[0];
      if (firstSid) {
        const existingIdx = ((store.builderConfig as any).conditionalLogic || []).findIndex(
          (r: any) =>
            r?.outcome?.type === "jumpTo" &&
            Array.isArray(r?.conditions) &&
            r.conditions.some(
              (c: any) =>
                (c?.fieldMode === "slide" && String(c?.selectedField) === firstSid) ||
                (!c?.fieldMode && String(c?.selectedField || "") === firstSid)
            )
        );
        if (existingIdx >= 0) {
          writeIndex = existingIdx;
        }
      }
      (store.builderConfig as any).conditionalLogic[writeIndex] = savedLogic;
      store.anyUnsavedChanges = true;
      console.log("savedLogic", store.builderConfig.conditionalLogic);
      emit("onSave");
      store.addToHistory();
      return;
    }
  }
  store.builderConfig.conditionalLogic[props.editIndex] = savedLogic;
  store.anyUnsavedChanges = true;
  console.log("savedLogic", store.builderConfig.conditionalLogic);
  emit("onSave");
  store.addToHistory();
};

const clonedFields = computed(() => {
  let fields: ConditionalSourceField[] = [];

  if (store.formId) {
    fields = cloneDeep(store.elements || []);
  } else if (store.surveyId || store.quizId) {
    fields = (store.slides || []).reduce(
      (acc: ConditionalSourceField[], slide: { slideData?: ConditionalSourceField[] }) => {
        if (slide.slideData && Array.isArray(slide.slideData)) {
          return [...acc, ...slide.slideData];
        }
        return acc;
      },
      []
    );
  }

  // Handle address settings for both forms and surveys
  const groupAddressIndex = fields.findIndex((item) => item.tag === "group_address");
  if (groupAddressIndex !== -1) {
    fields.splice(groupAddressIndex, 1, ...(store.addressSettings?.children || []));
  }

  return fields;
});

/**
 * Product & bump targets rendered as flat "field" options, reused by both the
 * IF condition-field dropdown and the THEN show/hide dropdown. Each product yields
 * a whole-product ("any variant") entry, plus one entry per variant when it has >1
 * variant; each bump yields a single entry. Labels resolve from the catalog details
 * loaded into `addedProductList` / `bumProductList` on form load. Labels are kept full;
 * `renderFieldOption` ellipsizes with CSS and shows the full text on hover.
 */
/**
 * IF-side product triggers: one entry per product and per bump, named plainly. The
 * pricing option is no longer a separate row here — it is chosen with the "Pricing
 * option is/is not" operators and the third dropdown, so a product with twelve variants
 * contributes one entry instead of thirteen.
 */
/** A single "Sell Product" entry; the product itself is chosen in the next dropdown. */
const productConditionOptions = computed(() => {
  if (countTotalProducts(productCatalog.value) === 0) {
    return [];
  }
  return [
    {
      value: PRODUCT_FIELD_SENTINEL,
      label: paymentElementLabel.value,
      rawLabel: paymentElementLabel.value,
      type: PRODUCT_CONDITION_FIELD_KIND,
      conditionalFieldKind: PRODUCT_CONDITION_FIELD_KIND,
      // Reuse the "Sell Products" icon so the row matches the payment element.
      icon: () =>
        h("img", {
          src: sellProductIcon,
          style: { width: "24px", height: "24px", objectFit: "contain" },
        }),
      picklistOptions: null,
    },
  ];
});

/** Products and bumps, grouped for the product dropdown. */
const productSelectOptions = computed(() => {
  const groups: any[] = [];
  if (productCatalog.value.products.length) {
    groups.push({
      type: "group",
      key: "main",
      label: t("form.conditonalLogic.mainProduct"),
      children: productCatalog.value.products.map((product) => ({
        label: product.name || t("form.conditonalLogic.untitledProduct"),
        value: buildProductConditionKey({
          kind: "product",
          productId: product.productId,
          priceId: null,
        }),
      })),
    });
  }
  if (productCatalog.value.bumps.length) {
    groups.push({
      type: "group",
      key: "bump",
      label: t("form.conditonalLogic.bumpProduct"),
      children: productCatalog.value.bumps.map((bump) => ({
        label: bump.name || t("form.conditonalLogic.untitledProduct"),
        value: buildProductConditionKey({
          kind: "bumpProduct",
          productId: bump.productId,
          priceId: bump.priceId,
        }),
      })),
    });
  }
  return groups;
});

const buildConditionalFieldOptions = (
  fields: ConditionalSourceField[]
): ConditionalFieldOption[] => {
  return fields
    .filter(({ type }) => {
      if (type === "url") {
        return false;
      }
      if (props.type === "jumpTo" || (props.type === "disqualifyLead" && store.surveyId)) {
        // expanded coverage for jumpTo & survey disqualify
        const excluded = [
          ...(store.formId ? standardFields.submit.group : []).map((x) => x.type),
          // Has no operators of its own; the "Sell Product" entry is the trigger.
          // Without this it shows as a second, identically-labelled, unusable row.
          ...(standardFields?.integration?.group || []).map((x) => x.type),
          "h1",
          "html",
          "img",
          "captcha",
          "source",
        ];
        return !excluded.includes(type);
      }

      const defaultExcludes = [
        ...(store.formId ? standardFields.submit.group : []).map((x) => x.type),
        ...(standardFields?.integration?.group || []).map((x) => x.type),
        "textbox_list",
        "h1",
        "html",
        "img",
        "captcha",
        "source",
      ];
      return !defaultExcludes.includes(type);
    })
    .flatMap((item) => {
      const iconRenderer = () => getOptionIconVNode(item as any);
      // Split T&C into TnC1 and TnC2 when both placeholders exist
      if (item.type === "terms_and_conditions" && item.placeholder && item.placeholder2) {
        return [
          {
            value: `${item.hiddenFieldQueryKey}_1`,
            label: "TnC1",
            type: item.type,
            conditionalFieldKind: resolveConditionalFieldKind(item),
            dataType: item.dataType,
            format: item.format,
            separator: item.separator,
            timeFormat: item.timeFormat,
            icon: iconRenderer,
            picklistOptions: null,
          },
          {
            value: `${item.hiddenFieldQueryKey}_2`,
            label: "TnC2",
            type: item.type,
            conditionalFieldKind: resolveConditionalFieldKind(item),
            dataType: item.dataType,
            format: item.format,
            separator: item.separator,
            timeFormat: item.timeFormat,
            icon: iconRenderer,
            picklistOptions: null,
          },
        ];
      }
      const isPictureChoice = isPictureChoiceField(item);
      return [
        {
          value: item.hiddenFieldQueryKey,
          label: item.label,
          type: isPictureChoice
            ? item.dataType === "CHECKBOX"
              ? "checkbox"
              : "radio"
            : item.type,
          conditionalFieldKind: isPictureChoice
            ? item.dataType === "CHECKBOX"
              ? "checkbox"
              : "radio"
            : resolveConditionalFieldKind(item),
          dataType: item.dataType,
          format: item.format,
          separator: item.separator,
          timeFormat: item.timeFormat,
          icon: iconRenderer,
          picklistOptions:
            item.picklistOptions || item?.picklistOptionsImage?.map((x) => x.label) || null,
          picklistOptionsImage: isPictureChoice ? item.picklistOptionsImage || [] : null,
          isPictureChoice,
          allowCustomOption: !!(item as any)?.allowCustomOption,
        },
      ];
    });
};

const conditionalFieldOptions = computed<ConditionalFieldOption[]>(() => [
  ...buildConditionalFieldOptions(clonedFields.value),
  ...productConditionOptions.value,
]);

const isCustomField = (field: ConditionalSourceField): boolean =>
  field.id !== undefined || field.custom === true;

const standardFieldOptions = computed<ConditionalFieldOption[]>(() =>
  buildConditionalFieldOptions(clonedFields.value.filter((field) => field.standard === true))
);

const customFieldOptions = computed<ConditionalFieldOption[]>(() =>
  buildConditionalFieldOptions(clonedFields.value.filter(isCustomField))
);

// Score options do not exist as slide elements, so expose them explicitly for
// quiz conditions alongside the questions collected from quiz slides.
const quizScoreFieldOptions = computed<ConditionalFieldOption[]>(() => {
  if (!store.quizId) {
    return [];
  }

  const scoreIcon = () => h(PieChart01Icon, { class: "h-5 w-5 text-gray-500" });
  const createScoreOption = (value: string, label: string): ConditionalFieldOption => ({
    value,
    label,
    type: "score",
    conditionalFieldKind: "score",
    dataType: "SCORE",
    icon: scoreIcon,
    picklistOptions: null,
  });

  return [
    createScoreOption("highestScore", t("form.conditonalLogic.highestScore")),
    createScoreOption("lowestScore", t("form.conditonalLogic.lowestScore")),
    createScoreOption("overAllScore", t("form.conditonalLogic.overallScore")),
    createScoreOption(CATEGORY_SCORE_FIELD, t("form.conditonalLogic.categoryScore")),
  ];
});

const quizCategoryOptions = computed(() =>
  (store.category || [])
    .filter((category) => category.id !== "overAllScore")
    .map((category) => ({ value: category.id, label: category.label }))
);

const scoreFormatOptions = computed(() => [
  { value: "percentage", label: t("form.conditonalLogic.percentage") },
  { value: "actual-score", label: t("form.conditonalLogic.actualScore") },
  { value: "out-of-10", label: t("form.conditonalLogic.outOf10") },
]);

// Keep a flat leaf list for value lookup because grouped select options contain
// headings that do not have condition values or field metadata.
const selectableFieldOptions = computed<ConditionalFieldOption[]>(() => {
  if (!store.quizId) {
    return conditionalFieldOptions.value;
  }

  return [...quizScoreFieldOptions.value, ...conditionalFieldOptions.value];
});

// Group only quiz selectors. Forms and surveys retain their existing flat list.
const fieldOptions = computed<Array<ConditionalFieldOption | ConditionalFieldGroupOption>>(() => {
  if (!store.quizId) {
    return conditionalFieldOptions.value;
  }

  const groups: ConditionalFieldGroupOption[] = [
    {
      key: "quizFields",
      label: t("form.conditonalLogic.quizFields"),
      type: "group",
      children: quizScoreFieldOptions.value,
    },
    {
      key: "standardFields",
      label: t("form.conditonalLogic.standardFields"),
      type: "group",
      children: standardFieldOptions.value,
    },
    {
      key: "customFields",
      label: t("common.customFields"),
      type: "group",
      children: customFieldOptions.value,
    },
  ];

  return groups.filter(({ children }) => children.length > 0);
});

/*
 * THEN side: the payment element is one dropdown entry; its products are chosen in
 * ConditionalProductPicker. All of it persists into the same `outcome.value` array
 * using keys ghl-form-element already understands.
 */

const productCatalog = computed(() =>
  buildProductCatalog(store.addedProductList, store.bumProductList)
);

/** True while the payment element is one of the chosen show/hide targets. */
const paymentTargetActive = ref(
  (() => {
    const { productKeys, hasEntireSection } = splitShowHideValue(outcomeValue.value);
    return hasEntireSection || productKeys.length > 0;
  })()
);

/** Chosen targets that are not the payment element. */
const plainShowHideKeys = computed(() => splitShowHideValue(outcomeValue.value).fieldKeys);

/** The picker's slice: `["payment"]` for the whole section, otherwise the product keys. */
const paymentTargetKeys = computed<string[]>({
  get() {
    const { productKeys, hasEntireSection } = splitShowHideValue(outcomeValue.value);
    return hasEntireSection ? [PAYMENT_SECTION_KEY] : productKeys;
  },
  set(next: string[]) {
    outcomeValue.value = [...plainShowHideKeys.value, ...next];
  },
});

const paymentElementLabel = computed(() => {
  const element = clonedFields.value.find(
    (item) => item.hiddenFieldQueryKey === PAYMENT_SECTION_KEY
  );
  const plain = (element?.label || "").replace(/<\/?[^>]+(>|$)/g, "");
  return plain || t("fields.integration.sellProducts");
});

const selectedProductCount = computed(() =>
  countSelectedProducts(
    keysToProductSelection(paymentTargetKeys.value, productCatalog.value),
    productCatalog.value
  )
);

/** "Entire section" shows no count; "Specific products" appends the selected count. */
const paymentTargetLabel = computed(() => {
  if (!paymentTargetActive.value || splitShowHideValue(outcomeValue.value).hasEntireSection) {
    return paymentElementLabel.value;
  }
  return `${paymentElementLabel.value} \u00b7 ${t("form.conditonalLogic.productsCountShort", {
    count: selectedProductCount.value,
  })}`;
});

/**
 * Non-product targets keep their historical persisted shape: a bare string for singular
 * Show/Hide, a list for the "Multiple" variants.
 */
const writePlainFieldKeys = (keys: string[]): string | string[] =>
  showMultiple.value ? keys : keys[0] ?? "";

/**
 * The THEN dropdown's own model. The payment element is one entry there; its product
 * detail lives in the picker, so selecting or clearing it seeds/clears that slice too.
 *
 * Singular Show/Hide binds a single value, and that stays true with a product target:
 * one field is chosen here, while the picker underneath may select many products. The
 * outcome then persists as a list under a singular hideType, which the runtime reads
 * the same way (`Array.isArray(value) ? value : [value]`, and `hideType.includes(...)`).
 */
const showHideSelection = computed({
  get(): string | string[] {
    const targets = paymentTargetActive.value
      ? [...plainShowHideKeys.value, PAYMENT_SECTION_KEY]
      : [...plainShowHideKeys.value];
    return showMultiple.value ? targets : targets[targets.length - 1] ?? "";
  },
  set(next: string | string[] | null) {
    const chosen = (Array.isArray(next) ? next : [next]).filter(
      (key): key is string => typeof key === "string" && key.length > 0
    );
    const wantsPayment = chosen.includes(PAYMENT_SECTION_KEY);
    const fieldKeys = chosen.filter((key) => key !== PAYMENT_SECTION_KEY);

    if (!wantsPayment) {
      paymentTargetActive.value = false;
      outcomeValue.value = writePlainFieldKeys(fieldKeys);
      return;
    }
    if (!paymentTargetActive.value) {
      paymentTargetActive.value = true;
      // Default to the whole section, matching the picker's default tab.
      outcomeValue.value = [...fieldKeys, PAYMENT_SECTION_KEY];
      return;
    }
    outcomeValue.value = [...fieldKeys, ...paymentTargetKeys.value];
  },
});

/** Switching to a singular hideType keeps one target; the payment one wins if selected. */
watch(showMultiple, (multiple) => {
  if (multiple) {
    return;
  }
  outcomeValue.value = paymentTargetActive.value
    ? [...paymentTargetKeys.value]
    : plainShowHideKeys.value[0] ?? "";
});

/**
 * Specific-products mode blocks Save when nothing is ticked, or when a target points at
 * a product that no longer exists — the merchant has to resolve it before the rule can
 * be trusted. Deleted targets only count once the catalog request has resolved.
 */
const isProductSelectionIncomplete = computed(() => {
  if (props.type !== "showHideFields" || !paymentTargetActive.value) {
    return false;
  }
  if (splitShowHideValue(outcomeValue.value).hasEntireSection) {
    return false;
  }
  if (paymentTargetKeys.value.length === 0) {
    return true;
  }
  return (
    store.getProductListAPIProcessed &&
    keysToProductSelection(paymentTargetKeys.value, productCatalog.value).unknownKeys.size > 0
  );
});

const showHideFieldOptions = computed(() => {
  const baseOptions = clonedFields.value
    .filter(({ type }) => {
      const excluded = [
        ...(store.formId ? standardFields.submit.group : []).map((x) => x.type),
        // Exclude captcha from Show/Hide targets
        "captcha",
      ];
      return !excluded.includes(type);
    })
    .flatMap((item) => {
      // Use a renderer function to generate a fresh icon VNode per render
      const iconRenderer = () => getOptionIconVNode(item as any);
      const plainLabel = (item.label || "").replace(/<\/?[^>]+(>|$)/g, "");
      const rawLabel = plainLabel;

      // Split T&C into two targets when both placeholders exist
      if (item.type === "terms_and_conditions" && item.placeholder && item.placeholder2) {
        return [
          {
            value: `${item.hiddenFieldQueryKey}_1`,
            label: "TnC1",
            rawLabel: "TnC1",
            type: item.type,
            icon: iconRenderer,
            picklistOptions: null,
          },
          {
            value: `${item.hiddenFieldQueryKey}_2`,
            label: "TnC2",
            rawLabel: "TnC2",
            type: item.type,
            icon: iconRenderer,
            picklistOptions: null,
          },
        ];
      }

      return [
        {
          value: item.hiddenFieldQueryKey,
          label: plainLabel,
          rawLabel,
          type: item.type,
          icon: iconRenderer,
          picklistOptions:
            item.picklistOptions || item?.picklistOptionsImage?.map((x) => x.label) || null,
        },
      ];
    });

  // Products are no longer flat rows here: the payment element is a single target and
  // ConditionalProductPicker drills into its products/variants/bumps. The option label
  // carries the selected count so the dropdown tag reads "Sell Products - 2 products".
  return baseOptions.map((option) =>
    option.value === PAYMENT_SECTION_KEY
      ? { ...option, label: paymentTargetLabel.value, rawLabel: paymentTargetLabel.value }
      : option
  );
});

// Jump To target type (Question or Slide)
const jumpTargetType = ref(
  (store.builderConfig.conditionalLogic?.[props.editIndex]?.outcome as any)?.jumpTargetType ||
    "question"
);

const questionOptions = computed(() => {
  if (!store.surveyId) {
    return [];
  }
  return clonedFields.value.map((item) => ({ value: item.hiddenFieldQueryKey, label: item.label }));
});

// IF (basis) slide options: no "Submit Survey"
const ifSlideOptions = computed(() => {
  if (!store.surveyId) {
    return [];
  }
  return store.slides.map((slide, index) => ({
    value: slide.id,
    label: `P${index + 1} - ${slide.slideName}`,
  }));
});

// Jump target slide options: exclude any selected slide(s) in IF; no "Submit Survey"
const jumpSlideOptions = computed(() => {
  if (!store.surveyId) {
    return [];
  }
  const excluded = new Set(
    (conditions.value || [])
      .filter((c: any) => c?.fieldMode === "slide" && c?.selectedField)
      .map((c: any) => String(c.selectedField))
  );
  return store.slides
    .map((slide, index) => ({ value: slide.id, label: `P${index + 1} - ${slide.slideName}` }))
    .filter((opt) => !excluded.has(String(opt.value)));
});

/**
 * Per condition, not one shared ref: a shared list meant editing one row overwrote the
 * options every other row rendered from, leaving their operators unlabelled.
 */
const buildOperationOptions = (fieldKey?: string | null) => {
  const selectedType = lookupFieldOption(fieldKey)?.conditionalFieldKind;
  if (isRankedCategoryField(fieldKey)) {
    return allOperationOptions.filter((option) => RANKED_CATEGORY_OPERATIONS.has(option.value));
  }
  return allOperationOptions.filter((option) => {
    if (isQuizScoreField(fieldKey) && shouldHideInput(option.value)) {
      return false;
    }
    if (!selectedType || !option.types.includes(selectedType)) {
      return false;
    }
    // Bumps and single-option products have nothing to choose between.
    if (isPricingOptionOperator(option.value)) {
      return supportsPricingOptions(fieldKey, productCatalog.value);
    }
    return true;
  });
};

const operationOptionsFor = (condition: ConditionRow) =>
  buildOperationOptions(condition.selectedField);
const fieldOptionByValue = computed(
  () => new Map(selectableFieldOptions.value.map((option) => [option.value, option]))
);

/**
 * Resolve a stored field key to its dropdown option.
 *
 * A product condition stores a pricing-option key (`product::<id>::<priceId>`) while the
 * dropdown only lists one entry per product (`product::<id>::*`), so a direct lookup
 * misses. Falling back to the product identity keeps the field type resolvable — without
 * it the operator list filters to nothing and the dropdown renders "No Data".
 */
const lookupFieldOption = (key?: string | null): ConditionalFieldOption | undefined => {
  if (!key) {
    return undefined;
  }
  const direct = fieldOptionByValue.value.get(key);
  if (direct) {
    return direct;
  }
  // Product targets all resolve to the one "Sell Product" entry; without this the
  // field type is unknown and the operator list filters to empty.
  return isProductConditionField(key)
    ? fieldOptionByValue.value.get(PRODUCT_FIELD_SENTINEL)
    : undefined;
};

const getSelectedFieldOption = (condition): ConditionalFieldOption | undefined =>
  lookupFieldOption(condition.selectedField);

const getType = (condition) => {
  return getSelectedFieldOption(condition)?.conditionalFieldKind || null;
};

const isPictureChoiceCondition = (condition): boolean =>
  getSelectedFieldOption(condition)?.isPictureChoice === true;

const getPictureChoicePicklistOptions = (
  selectedOption: ConditionalFieldOption
) => {
  const imageOptions = selectedOption.picklistOptionsImage ?? [];

  if (imageOptions.length) {
    return imageOptions.map((item) => ({
      value: item.label,
      label: item.label,
      imageSrc: item.thumbnailUrl || item.src || emptyOptionImage,
      isPictureChoice: true,
    }));
  }

  return (selectedOption.picklistOptions ?? []).map((item) => ({
    value: item,
    label: item,
    imageSrc: emptyOptionImage,
    isPictureChoice: true,
  }));
};

const getPicklistOptions = (condition) => {
  if (!condition.selectedField) {
    return [];
  }
  const selectedOption = getSelectedFieldOption(condition);
  const baseOptions = selectedOption?.isPictureChoice
    ? getPictureChoicePicklistOptions(selectedOption)
    : selectedOption?.picklistOptions?.map((item) => ({ value: item, label: item })) || [];
  const selectedType = getType(condition);
  const normalizedSelectedType =
    typeof selectedType === "string" ? selectedType.toLowerCase() : selectedType;
  if (
    (normalizedSelectedType === "radio" ||
      normalizedSelectedType === "checkbox" ||
      selectedOption?.isPictureChoice) &&
    !!selectedOption?.allowCustomOption
  ) {
    return [
      ...baseOptions,
      selectedOption.isPictureChoice
        ? {
            value: "Other",
            label: t("common.other"),
            imageSrc: otherOptionImage,
            isPictureChoice: true,
          }
        : { value: "Other", label: t("common.other") },
    ];
  }
  return baseOptions;
};

const updateOptions = (condition, value, oldSelectedField = null) => {
  if (!value) {
    return;
  }

  const oldSelectedType = oldSelectedField
    ? lookupFieldOption(oldSelectedField)?.conditionalFieldKind
    : null;
  const selectedFieldOption = lookupFieldOption(value);
  const selectedType = selectedFieldOption?.conditionalFieldKind;
  if (isRankedCategoryField(value)) {
    condition.scoreFormat = undefined;
    condition.inputValue = null;
  } else if (isNumericScoreField(value)) {
    condition.scoreFormat ||= "percentage";
    if (value !== CATEGORY_SCORE_FIELD) {
      condition.categoryId = undefined;
    }
  } else {
    condition.categoryId = undefined;
    condition.scoreFormat = undefined;
  }

  if (oldSelectedField && oldSelectedType !== selectedType) {
    condition.inputValue = "";
    if (
      selectedType &&
      ["single_options", "radio", "checkbox", "multiple_options"].includes(selectedType)
    ) {
      condition.inputValue = getPicklistOptions(condition)?.[0]?.value || "";
    }
    if (selectedType === "date") {
      condition.inputValue = dayjs().format("YYYY-MM-DD");
    }
    condition.rangeStart = undefined;
    condition.rangeEnd = undefined;
    condition.includeStart = true;
    condition.includeEnd = true;
    condition.dateFormat = undefined;
  }

  showPhoneAlert.value = selectedType === "phone" || value === "phone";

  // Ensure selected operation is valid for this condition's own options
  const available = buildOperationOptions(value);
  if (!available.some((o) => o.value === condition.selectedOperation)) {
    condition.selectedOperation = available[0]?.value || null;
  }
};

/*
 * IF side: four operators in the UI persist as two. "Pricing option is/is not" writes a
 * variant key, the rest write the any-variant key. See product-condition-operators.ts.
 */

/** What the field dropdown binds to: every product target shows as "Sell Product". */
const conditionFieldValue = (condition: ConditionRow): string | null =>
  toConditionFieldValue(condition.selectedField);

/** True while this condition targets a product or bump. */
const isProductCondition = (condition: ConditionRow): boolean =>
  isProductConditionField(condition.selectedField);

/** What the product dropdown binds to. */
const conditionProductValue = (condition: ConditionRow): string | null =>
  toProductSelectValue(condition.selectedField);

/** "Sell Product" seeds the first product; any other field clears back to normal. */
const setConditionField = (condition: ConditionRow, value: string) => {
  if (value !== PRODUCT_FIELD_SENTINEL) {
    const previous = condition.selectedField;
    condition.selectedField = value;
    updateOptions(condition, value, previous);
    // Quiz edits retain a valid operator; updateOptions replaces incompatible ones.
    if (!store.quizId) {
      condition.selectedOperation = buildOperationOptions(value)[0]?.value ?? null;
    }
    return;
  }

  const firstTarget = firstProductTargetKey(productCatalog.value);
  if (!firstTarget) {
    return;
  }
  const previous = condition.selectedField;
  condition.selectedField = firstTarget;
  condition.selectedOperation = CONDITIONAL_OPERATIONS.IS_SELECTED;
  condition.inputValue = "";
  updateOptions(condition, firstTarget, previous);
};

/** Switching product keeps the operator's intent where the new product supports it. */
const setConditionProduct = (condition: ConditionRow, productKey: string) => {
  const applied = applyProductSelection(
    productKey,
    resolveProductIfOperator(condition.selectedField, condition.selectedOperation),
    productCatalog.value
  );
  if (!applied) {
    return;
  }
  condition.selectedField = applied.selectedField;
  condition.selectedOperation = applied.selectedOperation;
  updateOptions(condition, applied.selectedField);
};

/** What the operator dropdown shows: one of the four product operators, or the raw one. */
const conditionOperatorValue = (condition: ConditionRow): string | null =>
  resolveProductIfOperator(condition.selectedField, condition.selectedOperation) ??
  condition.selectedOperation ??
  null;

const pricingOptionsFor = (condition: ConditionRow) =>
  pricingOptionsForField(condition.selectedField, productCatalog.value).map((variant) => ({
    label: variant.name || t("form.conditonalLogic.deletedVariant"),
    value: variant.priceId,
  }));

const showsPricingOptions = (condition: ConditionRow): boolean =>
  isPricingOptionOperator(conditionOperatorValue(condition));

const conditionPricingOption = (condition: ConditionRow): string | null =>
  resolvePricingOptionId(condition.selectedField);

const setConditionOperator = (condition: ConditionRow, value: string) => {
  const applied = applyProductIfOperator(
    String(condition.selectedField || ""),
    value as ProductIfOperator,
    // Switching into pricing mode defaults to the option already chosen, else the first.
    resolvePricingOptionId(condition.selectedField) ??
      pricingOptionsFor(condition)[0]?.value ??
      null
  );
  if (!applied) {
    condition.selectedOperation = value;
    return;
  }
  condition.selectedField = applied.selectedField;
  condition.selectedOperation = applied.selectedOperation;
};

const setConditionPricingOption = (condition: ConditionRow, priceId: string) => {
  const applied = applyProductIfOperator(
    String(condition.selectedField || ""),
    conditionOperatorValue(condition) as ProductIfOperator,
    priceId
  );
  if (applied) {
    condition.selectedField = applied.selectedField;
    condition.selectedOperation = applied.selectedOperation;
  }
};

const hideInput = (condition) => shouldHideInput(condition.selectedOperation);
const isScoreCondition = (condition: ConditionRow): boolean =>
  isQuizScoreField(condition.selectedField);
const isCategoryScoreCondition = (condition: ConditionRow): boolean =>
  condition.selectedField === CATEGORY_SCORE_FIELD;
const isRankedCategoryCondition = (condition: ConditionRow): boolean =>
  isRankedCategoryField(condition.selectedField);
const isNumericScoreCondition = (condition: ConditionRow): boolean =>
  isNumericScoreField(condition.selectedField);

const resolveOptionIcon = (option: any): VNode | null => {
  try {
    const maybe = typeof option.icon === "function" ? option.icon() : option.icon;
    return maybe || null;
  } catch {
    return null;
  }
};

// Long option labels are likely to be visually truncated; only then do we attach a
// tooltip. Kept as a char-length threshold (tunable) to stay deterministic/testable.
const OPTION_LABEL_TOOLTIP_THRESHOLD = 32;
// Show delay (ms) so the tooltip appears only on a deliberate hover, not a pass-over.
const OPTION_TOOLTIP_SHOW_DELAY_MS = 400;

const renderFieldOption = ({ node, option }: { node: VNode; option: any }) => {
  if (option.type === "group") {
    return cloneVNode(node, { class: "my-[2px] bg-gray-100" });
  }

  const fullText = option.rawLabel || option.label || "";
  const row = h("div", { class: "relative flex items-center w-full min-w-0" }, [
    h(
      "div",
      {
        class: [
          "option-icon ml-2 shrink-0 flex items-center justify-center",
          option.type === "rating" ? "w-8 h-8 ml-[4px] mr-[-4px]" : "w-6 h-6",
        ].join(" "),
      },
      [resolveOptionIcon(option)]
    ),
    // min-w-0 lets the label shrink so `truncate` ellipsizes only on real overflow;
    // full text still shows whenever it fits the available width.
    h("div", { class: "w-full min-w-0 truncate" }, [node]),
  ]);

  // Short labels always fit — skip the tooltip entirely (no hover noise, no overhead).
  if (fullText.length <= OPTION_LABEL_TOOLTIP_THRESHOLD) {
    return row;
  }

  // Body-teleported tooltip with the full label (no clipping inside the dropdown
  // popover). Small show delay so it only appears on a deliberate hover.
  return h(
    UITooltip as any,
    { trigger: "hover", placement: "top", delay: OPTION_TOOLTIP_SHOW_DELAY_MS },
    {
      trigger: () => row,
      default: () => fullText,
    }
  );
};

/**
 * naive routes group headers through `renderOption` too, so they are handled here: a
 * header gets the grey band and no tooltip, options keep the full-label tooltip.
 */
const renderProductOption = ({ node, option }: { node: VNode; option }) => {
  if (option?.type === "group") {
    return h(
      "div",
      { class: "bg-gray-100 px-3 py-2 text-sm font-medium text-gray-600" },
      option.label
    );
  }
  // naive fills both the hovered row and the current value; selection already shows as
  // blue text plus a checkmark. Inline because the menu is teleported out of scope.
  return renderOptions({
    node: cloneVNode(node, { style: { backgroundColor: "transparent" } }),
    option,
  });
};

const renderOptions = ({ node, option }: { node: VNode; option }) =>
  h(
    UITooltip,
    { placement: "left-start" },
    {
      trigger: () => node,
      default: () => option.label,
    }
  );

const renderPictureChoiceOptionLabel = (option) => {
  const label = String(option.label ?? "");

  return h("div", { class: "picture-choice-condition-label flex min-w-0 items-center gap-2 py-2" }, [
    h("img", {
      src: option.imageSrc || emptyOptionImage,
      alt: "",
      "aria-hidden": "true",
      class:
        "picture-choice-condition-thumbnail h-10 w-10 shrink-0 rounded object-cover",
    }),
    h(
      "span",
      { class: "min-w-0 truncate text-base font-medium text-gray-900" },
      label
    ),
  ]);
};
/** Use condition's stored format when setting datepicker value (parsing). */
const dateFormatForParsing = (condition: any) =>
  dateTypeFieldFormatDayjsForCondition(store, condition);

const dateTypeFieldValue = (condition: any) =>
  getDateTypeFieldValue(store, condition.selectedField, condition.inputValue, condition.dateFormat);

const dateFormatUI = (condition: any) => resolveDateTypeFieldFormat(store, condition.selectedField);

/** Use store format when writing date to condition; also set condition.dateFormat to this. */
const dateFormatDayjs = (condition: any) =>
  resolveDateTypeFieldFormatDayjs(store, condition.selectedField);

const dateFormatForSave = (condition: ConditionRow): string | undefined => {
  const selectedType = getType(condition);
  if (selectedType === "date" || selectedType === "date_time") {
    return dateFormatDayjs(condition);
  }
  return condition?.dateFormat;
};

const normalizeDateConditionValueForSave = (
  condition: ConditionRow,
  value: unknown
): unknown => {
  if (getType(condition) !== "date" || value === undefined || value === null || value === "") {
    return value;
  }
  // Only a picker timestamp or a stored date string can be reformatted; anything
  // else round-trips untouched rather than becoming an Invalid Date.
  if (typeof value !== "number" && typeof value !== "string") {
    return value;
  }

  const parsed =
    typeof value === "number" ? dayjs(value) : dayjs(value, dateFormatForParsing(condition));
  return parsed.isValid() ? parsed.format(dateFormatDayjs(condition)) : value;
};

const safeDayjs = (date: string, condition: any) => {
  const value = dayjs(date, dateFormatForParsing(condition));
  return value.isValid() ? value.valueOf() : null;
};

const timeFormat = (condition: ConditionRow): string | number | undefined =>
  getSelectedFieldOption(condition)?.timeFormat;

const timePickerFormat = (condition: ConditionRow): string => timeFormatForUI(timeFormat(condition));
const temporalPickerPlacement = ["bottom", "end"].join("-");

const isJumpToSurveyCondition = (): boolean => props.type === "jumpTo" && !!store.surveyId;

const isSlideBasisCondition = (condition: ConditionRow): boolean =>
  isJumpToSurveyCondition() && condition.fieldMode === "slide";

const isDateTimeBetweenValueCondition = (condition: ConditionRow): boolean =>
  !isJumpToSurveyCondition() &&
  !isSlideBasisCondition(condition) &&
  getType(condition) === "date_time" &&
  condition.selectedOperation === "between";

const isDateTimeSingleValueCondition = (condition: ConditionRow): boolean =>
  !isJumpToSurveyCondition() &&
  !isSlideBasisCondition(condition) &&
  getType(condition) === "date_time" &&
  condition.selectedOperation !== "between";

const isQuizScoreBetweenCondition = (condition: ConditionRow): boolean =>
  condition.selectedOperation === "between" && isNumericScoreCondition(condition);

const conditionRowClass = (condition: ConditionRow): string => {
  if (isJumpToSurveyCondition()) {
    // gap-y too: a product condition adds a fifth control, which wraps to a second row.
    return condition.fieldMode === "slide"
      ? "mb-8 grid grid-cols-2 items-center gap-x-2 gap-y-2"
      : "mb-8 grid grid-cols-4 items-center gap-x-2 gap-y-2";
  }

  if (isScoreCondition(condition)) {
    return "mb-8 grid grid-cols-3 items-center gap-2 px-0.5";
  }

  return isDateTimeSingleValueCondition(condition)
    ? "mb-8 flex items-center gap-x-2 px-0.5"
    : isDateTimeBetweenValueCondition(condition)
    ? "mb-8 flex flex-wrap items-center gap-x-2 gap-y-2 px-0.5"
    : "mb-8 flex gap-x-2 px-0.5";
};

const selectedFieldClass = (condition: ConditionRow): string => {
  if (isJumpToSurveyCondition()) {
    return "w-full";
  }

  if (isDateTimeBetweenValueCondition(condition)) {
    return "w-[calc(50%-0.25rem)]";
  }

  if (isQuizScoreBetweenCondition(condition)) {
    return "w-full";
  }

  if (condition.selectedOperation === "between") {
    return "w-1/4";
  }

  return isDateTimeSingleValueCondition(condition) ? "min-w-0 flex-1" : "w-full";
};

const selectedOperationClass = (condition: ConditionRow): string => {
  if (isJumpToSurveyCondition()) {
    return "w-full";
  }

  if (isDateTimeBetweenValueCondition(condition)) {
    return "w-[calc(50%-0.25rem)]";
  }

  if (isQuizScoreBetweenCondition(condition)) {
    return "w-full";
  }

  if (condition.selectedOperation === "between") {
    return "w-1/4";
  }

  return isDateTimeSingleValueCondition(condition) ? "w-[10.5rem] shrink-0" : "w-full";
};

const dateTimeSingleValueClass = (condition: ConditionRow): string[] => [
  hideInput(condition) ? "invisible" : "visible",
  isDateTimeSingleValueCondition(condition) ? "flex w-[21.5rem] shrink-0 gap-2" : "flex w-full gap-2",
];

const dateTimeBetweenValueClass = (condition: ConditionRow): string =>
  isDateTimeBetweenValueCondition(condition)
    ? "grid w-full grid-cols-2 gap-2"
    : props.type === "jumpTo" && store.surveyId
    ? "grid w-full grid-cols-2 gap-2"
    : "grid w-1/2 grid-cols-2 gap-2";

const dateTimeBetweenPickerClass = "conditional-temporal-picker min-w-0 flex-1";
const dateTimeSingleDatePickerClass = "conditional-temporal-picker min-w-0 flex-[1.08_1_0]";
const dateTimeSingleTimePickerClass = "conditional-temporal-picker min-w-0 flex-1";

const timePickerValue = (value: unknown): number | null => {
  if (typeof value === "number") {
    return value;
  }
  const timeValue = toConditionTimeValue(value);
  if (!timeValue) {
    return null;
  }
  return toTimePickerTimestamp(timeValue, "24");
};

const dateTimeDatePickerValue = (value: unknown): number | null => {
  const parsed = parseConditionDateTimeValue(value);
  return parsed.dateValue;
};

const dateTimeTimePickerValue = (value: unknown): number | null => {
  const parsed = parseConditionDateTimeValue(value);
  return parsed.timeValue;
};

const defaultDateTimeDateValue = (): number => dayjs().startOf("day").valueOf();

const defaultDateTimeTimeValue = (): number => new Date(2000, 0, 1, 0, 0, 0, 0).getTime();

const mergeDateTimeValue = (
  currentValue: unknown,
  dateValue?: number | null,
  timeValue?: number | null
): string => {
  const hasDateUpdate = dateValue !== undefined;
  const hasTimeUpdate = timeValue !== undefined;
  const currentDate = dateTimeDatePickerValue(currentValue);
  const currentTime = dateTimeTimePickerValue(currentValue);
  const shouldDefaultDate = hasTimeUpdate && timeValue !== null;
  const shouldDefaultTime = hasDateUpdate && dateValue !== null;
  const nextDate = hasDateUpdate
    ? dateValue
    : currentDate ?? (shouldDefaultDate ? defaultDateTimeDateValue() : null);
  const nextTime = hasTimeUpdate
    ? timeValue
    : currentTime ?? (shouldDefaultTime ? defaultDateTimeTimeValue() : null);
  return toConditionDateTimeValue(nextDate, nextTime);
};
</script>

<template>
  <div class="divider h-[610px] overflow-auto">
    <div class="flex justify-between py-3 font-medium">
      {{ t("form.conditonalLogic.logic.if") }}
      <Trash02Icon
        v-if="conditions.length > 1"
        class="mr-3 h-4 w-4 cursor-pointer text-error-700"
        @click="deleteCondition(0)"
      />
    </div>
    <template v-for="(condition, index) in conditions" :key="index">
      <div class="mb-2 flex flex-col gap-y-2 text-base">
        <div :class="conditionRowClass(condition)">
          <!-- Jump To (Surveys): Field or Slide basis selector -->
          <UISelect
            v-if="props.type === 'jumpTo' && store.surveyId"
            :id="`fieldMode-${index}`"
            class="w-full"
            :value="condition.fieldMode || 'field'"
            :options="[
              { label: t('form.conditonalLogic.field'), value: 'field' },
              { label: t('form.conditonalLogic.slide'), value: 'slide' },
            ]"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectFieldOrSlide')"
            filterable
            @update:value="
              (v) => {
                condition.fieldMode = v;
                condition.selectedField = null;
                condition.inputValue = null;
                condition.rangeStart = undefined;
                condition.rangeEnd = undefined;
                condition.includeStart = true;
                condition.includeEnd = true;
                condition.dateFormat = undefined;
                condition.selectedOperation = 'isEqualTo';
              }
            "
          />
          <UISelect
            v-if="!(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide')"
            :id="`selectedField-${index}`"
            :class="selectedFieldClass(condition)"
            :value="conditionFieldValue(condition)"
            :options="fieldOptions"
            :render-option="renderFieldOption"
            :placeholder="t('form.conditonalLogic.selectField')"
            filterable
            @update:value="(value) => setConditionField(condition, value)"
          />
          <!-- Slide chooser when basis is Slide -->
          <UISelect
            v-else
            :id="`selectedSlide-${index}`"
            class="w-full"
            :value="condition.selectedField"
            :options="ifSlideOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectSlideToJumpTo')"
            filterable
            @update:value="
              (value) => {
                condition.selectedField = value;
                // No value input is required for slide-level IF
              }
            "
          />
          <!--
            Writes the real product/bump key into `selectedField`. Must stay after the
            slide chooser: it carries a v-if, and sitting between that v-if/v-else pair
            would re-pair the v-else with this condition.
          -->
          <UISelect
            v-if="isProductCondition(condition)"
            :id="`conditionProduct-${index}`"
            class="w-full"
            :value="conditionProductValue(condition)"
            :options="productSelectOptions"
            :render-option="renderProductOption"
            :placeholder="t('form.conditonalLogic.selectProduct')"
            filterable
            @update:value="(value) => setConditionProduct(condition, value)"
          />
          <UISelect
            v-if="isCategoryScoreCondition(condition)"
            :id="`categoryId-${index}`"
            class="w-full"
            :value="condition.categoryId"
            :options="quizCategoryOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectCategory')"
            filterable
            @update:value="(value) => (condition.categoryId = value)"
          />
          <!-- For jumpTo, operation is implicitly isEqualTo; no selector needed -->
          <UISelect
            v-if="
              props.type !== 'jumpTo' ||
              (props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'field')
            "
            :id="`selectedOperation-${index}`"
            :class="selectedOperationClass(condition)"
            :value="conditionOperatorValue(condition)"
            :options="operationOptionsFor(condition)"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectFieldState')"
            filterable
            :disabled="!condition.selectedField"
            @click="updateOptions(condition, condition.selectedField)"
            @update:value="(value) => setConditionOperator(condition, value)"
          />
          <!-- Takes the value slot, but persists into `selectedField`, not `inputValue`. -->
          <UISelect
            v-if="showsPricingOptions(condition)"
            :id="`pricingOption-${index}`"
            class="w-full"
            :value="conditionPricingOption(condition)"
            :options="pricingOptionsFor(condition)"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectPricingOption')"
            filterable
            @update:value="(value) => setConditionPricingOption(condition, value)"
          />
          <UISelect
            v-if="isRankedCategoryCondition(condition)"
            :id="`categoryId-${index}`"
            class="w-full"
            :value="condition.categoryId"
            :options="quizCategoryOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectCategory')"
            filterable
            @update:value="(value) => (condition.categoryId = value)"
          />
          <UISelect
            v-if="isNumericScoreCondition(condition) && !hideInput(condition)"
            :id="`scoreFormat-${index}`"
            class="w-full"
            :value="condition.scoreFormat"
            :options="scoreFormatOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.scoreFormat')"
            :disabled="!condition.selectedOperation"
            @update:value="(value) => (condition.scoreFormat = value)"
          />
          <!-- Value input renders only when basis is Field -->
          <UIDatepicker
            v-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date' &&
              condition.selectedOperation === 'between'
            "
            :id="`input_datepicker_range-${index}`"
            :class="[
              'conditional-temporal-picker',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-1/2',
            ]"
            type="daterange"
            :placement="temporalPickerPlacement"
            :shortcuts="dateRangeShortcuts()"
            :value="
              (condition.rangeStart && condition.rangeEnd && safeDayjs(condition.rangeStart, condition) && safeDayjs(condition.rangeEnd, condition)
                ? [
                    safeDayjs(condition.rangeStart, condition),
                    safeDayjs(condition.rangeEnd, condition),
                  ]
                : null) as any
            "
            :format="dateFormatUI(condition)"
            @update:value="
              (values) => {
                const [start, end] = values || [];
                const format = dateFormatDayjs(condition);
                condition.dateFormat = format;
                condition.rangeStart = start ? dayjs(start).format(format) : undefined;
                condition.rangeEnd = end ? dayjs(end).format(format) : undefined;
              }
            "
          />
          <UIDatepicker
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date'
            "
            id="input_datepicker"
            :class="[
              'conditional-temporal-picker',
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
            ]"
            :value="dateTypeFieldValue(condition)"
            :format="dateFormatUI(condition)"
            :placement="temporalPickerPlacement"
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:value="
              (value) => {
                const format = dateFormatDayjs(condition);
                condition.dateFormat = format;
                condition.inputValue = dayjs(value).format(format);
              }
            "
          />
          <div
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'time' &&
              condition.selectedOperation === 'between'
            "
            :class="
              props.type === 'jumpTo' && store.surveyId ? 'flex w-full gap-2' : 'flex w-1/2 gap-2'
            "
          >
            <UITimePicker
              :id="`inputTimeStart-${index}`"
              class="conditional-temporal-picker w-1/2"
              :value="timePickerValue(condition.rangeStart)"
              :format="timePickerFormat(condition)"
              :placement="temporalPickerPlacement"
              :placeholder="t('common.dateTime.timePlaceholder')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:value="(value) => (condition.rangeStart = toConditionTimeValue(value))"
            />
            <UITimePicker
              :id="`inputTimeEnd-${index}`"
              class="conditional-temporal-picker w-1/2"
              :value="timePickerValue(condition.rangeEnd)"
              :format="timePickerFormat(condition)"
              :placement="temporalPickerPlacement"
              :placeholder="t('common.dateTime.timePlaceholder')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:value="(value) => (condition.rangeEnd = toConditionTimeValue(value))"
            />
          </div>
          <UITimePicker
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'time'
            "
            :id="`inputTime-${index}`"
            :class="[
              'conditional-temporal-picker',
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
            ]"
            :value="timePickerValue(condition.inputValue)"
            :format="timePickerFormat(condition)"
            :placement="temporalPickerPlacement"
            :placeholder="t('common.dateTime.timePlaceholder')"
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:value="(value) => (condition.inputValue = toConditionTimeValue(value))"
          />
          <div
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date_time' &&
              condition.selectedOperation === 'between'
            "
            :class="dateTimeBetweenValueClass(condition)"
          >
            <div class="flex min-w-0 gap-2">
              <UIDatepicker
                :id="`inputDateTimeStartDate-${index}`"
                :class="dateTimeBetweenPickerClass"
                :value="dateTimeDatePickerValue(condition.rangeStart)"
                :format="dateFormatUI(condition)"
                :placement="temporalPickerPlacement"
                :aria-label="t('common.dateTime.rangeStart')"
                :disabled="!condition.selectedField || !condition.selectedOperation"
                @update:value="
                  (value) => {
                    condition.dateFormat = dateFormatDayjs(condition);
                    condition.rangeStart = mergeDateTimeValue(condition.rangeStart, value, undefined);
                  }
                "
              />
              <UITimePicker
                :id="`inputDateTimeStartTime-${index}`"
                :class="dateTimeBetweenPickerClass"
                :value="dateTimeTimePickerValue(condition.rangeStart)"
                :format="timePickerFormat(condition)"
                :placement="temporalPickerPlacement"
                :placeholder="t('common.dateTime.timePlaceholder')"
                :aria-label="t('common.dateTime.timeStart')"
                :disabled="!condition.selectedField || !condition.selectedOperation"
                @update:value="
                  (value) =>
                    (condition.rangeStart = mergeDateTimeValue(
                      condition.rangeStart,
                      undefined,
                      value
                    ))
                "
              />
            </div>
            <div class="flex min-w-0 gap-2">
              <UIDatepicker
                :id="`inputDateTimeEndDate-${index}`"
                :class="dateTimeBetweenPickerClass"
                :value="dateTimeDatePickerValue(condition.rangeEnd)"
                :format="dateFormatUI(condition)"
                :placement="temporalPickerPlacement"
                :aria-label="t('common.dateTime.rangeEnd')"
                :disabled="!condition.selectedField || !condition.selectedOperation"
                @update:value="
                  (value) => {
                    condition.dateFormat = dateFormatDayjs(condition);
                    condition.rangeEnd = mergeDateTimeValue(condition.rangeEnd, value, undefined);
                  }
                "
              />
              <UITimePicker
                :id="`inputDateTimeEndTime-${index}`"
                :class="dateTimeBetweenPickerClass"
                :value="dateTimeTimePickerValue(condition.rangeEnd)"
                :format="timePickerFormat(condition)"
                :placement="temporalPickerPlacement"
                :placeholder="t('common.dateTime.timePlaceholder')"
                :aria-label="t('common.dateTime.timeEnd')"
                :disabled="!condition.selectedField || !condition.selectedOperation"
                @update:value="
                  (value) =>
                    (condition.rangeEnd = mergeDateTimeValue(
                      condition.rangeEnd,
                      undefined,
                      value
                    ))
                "
              />
            </div>
          </div>
          <div
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date_time'
            "
            :class="dateTimeSingleValueClass(condition)"
          >
            <UIDatepicker
              :id="`inputDateTimeDate-${index}`"
              :class="dateTimeSingleDatePickerClass"
              :value="dateTimeDatePickerValue(condition.inputValue)"
              :format="dateFormatUI(condition)"
              :placement="temporalPickerPlacement"
              :aria-label="t('common.dateTime.rangeStart')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:value="
                (value) => {
                  condition.dateFormat = dateFormatDayjs(condition);
                  condition.inputValue = mergeDateTimeValue(condition.inputValue, value, undefined);
                }
              "
            />
            <UITimePicker
              :id="`inputDateTimeTime-${index}`"
              :class="dateTimeSingleTimePickerClass"
              :value="dateTimeTimePickerValue(condition.inputValue)"
              :format="timePickerFormat(condition)"
              :placement="temporalPickerPlacement"
              :placeholder="t('common.dateTime.timePlaceholder')"
              :aria-label="t('common.dateTime.timeFormatLabel')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:value="
                (value) =>
                  (condition.inputValue = mergeDateTimeValue(
                    condition.inputValue,
                    undefined,
                    value
                  ))
              "
            />
          </div>
          <UISelect
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              ['single_options', 'radio', 'checkbox', 'multiple_options'].includes(
                getType(condition)
              ) &&
              condition.selectedOperation !== 'between'
            "
            :id="`picklistOptions-${index}`"
            :class="[
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
              isPictureChoiceCondition(condition)
                ? 'picture-choice-condition-select'
                : '',
            ]"
            :value="condition.inputValue"
            :options="getPicklistOptions(condition)"
            :render-option="renderOptions"
            :render-label="
              isPictureChoiceCondition(condition)
                ? renderPictureChoiceOptionLabel
                : undefined
            "
            :placeholder="t('form.conditonalLogic.selectOption')"
            filterable
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:value="(value) => (condition.inputValue = value)"
          />
          <div
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              condition.selectedOperation === 'between' &&
              ['numerical', 'monetory', 'score', 'rating'].includes(getType(condition))
            "
            :class="
              isQuizScoreBetweenCondition(condition)
                ? 'contents'
                : props.type === 'jumpTo' && store.surveyId
                ? 'flex w-full gap-2'
                : 'flex w-1/2 gap-2'
            "
          >
            <UIInput
              :id="`inputValueStart-${index}`"
              :class="isQuizScoreBetweenCondition(condition) ? 'w-full' : 'w-1/2'"
              :model-value="condition.rangeStart"
              :placeholder="t('common.startValue')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:model-value="(value) => (condition.rangeStart = value)"
            />
            <UIInput
              :id="`inputValueEnd-${index}`"
              :class="isQuizScoreBetweenCondition(condition) ? 'w-full' : 'w-1/2'"
              :model-value="condition.rangeEnd"
              :placeholder="t('common.endValue')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:model-value="(value) => (condition.rangeEnd = value)"
            />
          </div>
          <!--
            Hidden with `invisible`, not removed, so value-less rows keep three columns.
            The pricing select already fills that column, so drop it there.
          -->
          <UIInput
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              !showsPricingOptions(condition) &&
              !isRankedCategoryCondition(condition)
            "
            :id="`inputValue-${index}`"
            :class="[
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
            ]"
            :model-value="condition.inputValue"
            :placeholder="t('form.conditonalLogic.typeValueHere')"
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:model-value="(value) => (condition.inputValue = value)"
          />
        </div>
        <div
          v-if="condition.selectedOperation === 'between'"
          class="-mt-6 mb-4 flex items-center gap-6 text-sm text-gray-700"
        >
          <div class="flex items-center gap-2">
            <UICheckbox
              id="include-start"
              :checked="condition.includeStart"
              @update:checked="(v) => (condition.includeStart = !!v)"
            />
            <span>{{
              includeStartText(
                condition,
                getType(condition),
                dateFormatUI(condition)?.toUpperCase(),
                timeFormat(condition)
              )
            }}</span>
          </div>
          <div class="flex items-center gap-2">
            <UICheckbox
              id="include-end"
              :checked="condition.includeEnd"
              @update:checked="(v) => (condition.includeEnd = !!v)"
            />
            <span>{{
              includeEndText(
                condition,
                getType(condition),
                dateFormatUI(condition)?.toUpperCase(),
                timeFormat(condition)
              )
            }}</span>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div :class="index === conditions.length - 1 ? 'w-[88%]' : 'w-3/5'">
            <div v-if="props.type === 'jumpTo'" class="font-medium text-gray-600">
              {{ t("form.conditonalLogic.logic.then") }}
            </div>
            <UITabs
              v-else
              default-value="then"
              type="segment"
              :value="index === conditions.length - 1 ? 'then' : sharedSelectedOperation"
              @update:value="(condition) => addCondition(condition, index)"
            >
              <UITabPane
                v-if="index === conditions.length - 1"
                name="then"
                :tab="t('form.conditonalLogic.logic.then')"
                >{{ t("form.conditonalLogic.logic.then") }}</UITabPane
              >
              <UITabPane
                name="and"
                :tab="t('form.conditonalLogic.logic.and')"
                :disabled="isConditionInvalid(condition)"
              >
                {{ t("form.conditonalLogic.logic.and") }}
              </UITabPane>
              <UITabPane
                name="or"
                :tab="t('form.conditonalLogic.logic.or')"
                :disabled="isConditionInvalid(condition)"
              >
                {{ t("form.conditonalLogic.logic.or") }}</UITabPane
              >
            </UITabs>
          </div>
          <Trash02Icon
            v-if="index !== conditions.length - 1"
            class="mr-3 h-4 w-4 cursor-pointer text-error-700"
            @click="deleteCondition(index + 1)"
          />
        </div>
      </div>
    </template>
    <UIAlert v-if="showPhoneAlert" id="warning-message" type="warning">
      <template #content>{{ t("form.conditonalLogic.phoneAlertMessage") }}</template>
    </UIAlert>
    <div id="output" class="mb-3 mt-3">
      <div class="mb-2 text-xs font-medium text-gray-700">
        <div class="mb-3 flex">
          <div v-if="props.type === 'disqualifyLead'" class="flex w-full gap-2 text-gray-900">
            <UISelect
              v-if="store.surveyId"
              id="disqualify-timing"
              v-model:value="disqualifyTiming"
              class="w-full"
              :options="disqualifyTimingOptions"
              :render-option="renderOptions"
              :placeholder="t('common.select')"
            />
            <UISelect
              :id="`disqualify-action`"
              v-model:value="disqualifyAction"
              class="w-full"
              :options="disqualifyActionOptions"
              :render-option="renderOptions"
              :placeholder="t('common.select')"
              @update:value="() => (outcomeValue = '')"
            />
          </div>
          <div v-if="props.type === 'displayCustomMessage'">
            {{ t("form.conditonalLogic.showCustomMessage") }}
          </div>
        </div>
        <div ref="clEditorContainer">
          <RichTextEditor
            v-if="
              props.type === 'displayCustomMessage' ||
              (props.type === 'disqualifyLead' && disqualifyAction === 'showCustomMessage')
            "
            v-model:modelValue="outcomeValue"
            size="mid"
            :is-submit-message="true"
            :modal-container="clEditorContainer"
          />
        </div>
      </div>
      <div
        v-if="
          props.type === 'redirectToUrl' ||
          (props.type === 'disqualifyLead' && disqualifyAction === 'openUrl')
        "
        class="mb-2 text-xs font-medium text-gray-700"
      >
        {{ t("form.conditonalLogic.openBelowUrl") }}
        <UIInput
          id="redirectToUrlInput"
          v-model="outcomeValue"
          class="my-3 w-full font-normal"
          :placeholder="t('common.exampleUrlPlaceholder')"
        />
        <div class="text-xs font-normal text-gray-600">
          {{ t("common.addUrlLike") }}<span class="underline">{{ URL_EXAMPLES.simple }}</span
          >, <span class="underline">{{ URL_EXAMPLES.https }}</span
          >, <span class="underline">{{ URL_EXAMPLES.withPath }}</span> {{ t("common.or") }}
          <span class="underline">{{ URL_EXAMPLES.fullPath }}</span
          >.
        </div>
      </div>
      <div v-if="props.type === 'showHideFields'" class="mb-2 text-xs font-medium text-gray-700">
        <div class="mb-8 flex gap-x-2">
          <UISelect
            id="showHideFieldType"
            v-model:value="hideType"
            class="w-1/3"
            :options="showHideOptions"
            :render-option="renderOptions"
            :placeholder="t('common.select')"
            filterable
          />
          <UISelect
            id="selectedFieldShowHide"
            v-model:value="showHideSelection"
            :options="showHideFieldOptions"
            :render-option="renderFieldOption"
            :placeholder="t('common.select')"
            filterable
            :multiple="showMultiple"
            type="default"
          />
        </div>
        <ConditionalProductPicker
          v-if="paymentTargetActive"
          id="conditional-product-picker"
          v-model="paymentTargetKeys"
          class="mb-8"
          :catalog="productCatalog"
          :section-label="paymentElementLabel"
          :hide-type="hideType"
          :catalog-loaded="store.getProductListAPIProcessed"
        />
      </div>
      <div v-if="props.type === 'jumpTo'" class="mb-2 text-xs font-medium text-gray-700">
        {{ t("form.conditonalLogic.jumpToDescription") }}
        <div class="mb-8 mt-2 flex gap-x-2">
          <UISelect
            id="jumpToSlide"
            v-model:value="jumpTarget"
            class="w-full"
            :options="jumpSlideOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectSlideToJumpTo')"
            filterable
          />
        </div>

        <UIAlert v-if="cycleErrorMessage" id="cycle-error" type="error">
          <template #content>{{ cycleErrorMessage }}</template>
        </UIAlert>
      </div>
    </div>
  </div>
  <UIModalFooter
    id="modal-footer"
    :positive-text="t('common.save')"
    :disabled="isSaveDisabled"
    :loading="false"
    @positive-click="onSave"
  />
</template>

<style scoped>
.divider {
  border-bottom: 1px solid #eaecf0;
  width: 100%;
}
:deep(.icon-custom) {
  border: 0;
  box-shadow: none;
  display: block;
}
.n-tabs {
  width: 25%;
}
:deep(.n-tabs-tab) {
  border-radius: 6px !important;
}

/*
 * The And/Or switcher highlight.
 *
 * naive renders the segment highlight as an absolutely positioned "capsule" that is
 * only measured on mount, on value change, and on rail resize. The tab set here is not
 * stable: a condition that stops being the last one loses its "Then" tab, so the active
 * element can be missing at measure time and the capsule stays parked over whatever it
 * covered before — sitting on top of "And"/"Or" instead of behind the active segment.
 *
 * Painting the highlight on the active tab itself removes the whole failure mode: there
 * is nothing left to position, so it cannot drift out of alignment.
 */
:deep(.n-tabs-capsule) {
  display: none;
}

:deep(.n-tabs-tab--active) {
  background-color: #fff;
  box-shadow: 0 1px 2px 0 rgb(16 24 40 / 5%);
}
.n-tab-pane {
  display: none;
}
:deep(.n-tabs-rail) {
  border-radius: 6px;
}
:deep(.n-avatar) {
  display: none !important;
}
:deep(.n-tag__content) {
  padding-left: 6px;
}

.conditional-temporal-picker :deep(.n-input) {
  min-height: 40px;
}

.conditional-temporal-picker :deep(.n-input-wrapper),
.conditional-temporal-picker :deep(.n-input__input),
.conditional-temporal-picker :deep(.n-input__suffix) {
  align-items: center;
}

.conditional-temporal-picker :deep(.n-input__suffix) {
  display: inline-flex;
  height: 100%;
  line-height: 1;
}

.conditional-temporal-picker :deep(.n-date-picker-icon),
.conditional-temporal-picker :deep(.n-time-picker-icon),
.conditional-temporal-picker :deep(.n-base-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.conditional-temporal-picker :deep(.n-date-picker-icon svg),
.conditional-temporal-picker :deep(.n-time-picker-icon svg),
.conditional-temporal-picker :deep(.n-base-icon svg) {
  display: block;
}

/* Normalize option icons to consistent size */
.option-icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.option-icon :deep(svg),
.option-icon :deep(img) {
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
  object-fit: contain;
}

.picture-choice-condition-select
  :deep(.n-base-selection-label__render-label .picture-choice-condition-thumbnail) {
  display: none;
}
.picture-choice-condition-select
  :deep(.n-base-selection-label__render-label .picture-choice-condition-label) {
  padding-bottom: 0;
  padding-top: 0;
}
</style>
